import { cn } from '@/lib/utils';
import type { SnakeCurrencyDto } from '@/lib/models';

export interface SnakeCurrencyTabsProps {
  /** Available currencies — each tab IS an instrument/currency option. */
  currencies: SnakeCurrencyDto[];
  /** Active instrument code, or `null` before the first selection. */
  activeInstrument: string | null;
  /** Fired with the selected instrument code. */
  onChange: (instrument: string) => void;
  className?: string;
}

/**
 * Currency tab row for the "Short Markets" lobby widget (Figma 5751-141388
 * "Mobile_chosen Category").
 *
 * A horizontal scrollable row of outlined pills — each pill IS a
 * currency/instrument option (Bitcoin, Gold, …). Selecting a pill switches the
 * single card below to that currency. The selected pill has a purple border +
 * purple text; the rest carry a grey divider border + grey text.
 */
export function SnakeCurrencyTabs({ currencies, activeInstrument, onChange, className }: SnakeCurrencyTabsProps) {
  if (currencies.length === 0) return null;

  return (
    <div className={cn('flex w-full items-center gap-2 overflow-x-auto scrollbar-hide', className)}>
      {currencies.map((c) => {
        const active = c.instrument === activeInstrument;
        const label = c.name ?? c.shortName ?? c.instrument;
        return (
          <button
            key={c.instrument}
            type="button"
            onClick={() => onChange(c.instrument)}
            aria-pressed={active}
            className={cn(
              'flex h-[28px] min-w-[80px] shrink-0 items-center justify-center whitespace-nowrap rounded-[49px] border border-solid bg-surface px-4 text-[16px] capitalize tracking-[-0.32px] transition-colors',
              active ? 'border-purple text-purple' : 'border-neutral-100 text-ink-muted',
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
